import { debounce } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { useCallback, useMemo } from 'react';
import { ITEM_CLICK_DEBOUNCE_MS } from 'src/components/Navbar/DesktopDrawer/InboxPane/inbox-pane.constants';
import { Notification } from 'src/graphql/entities/notification';
import {
  getNotificationConversationFriendlyId,
  getNotificationEntityName,
  getNotificationTargetPath,
} from 'src/graphql/entities/notification/notification-target';
import { EntityNames } from 'src/graphql/types';
import { useSelectedConversation } from 'src/store/selected-conversation';
import { type TabEntity, TabType, useGlobalTabs } from 'src/utils/hooks/use-global-tabs';
import { useNavigateRoute } from 'src/utils/routing';

function getNotificationTab(
  notification: Notification,
  entityName: EntityNames | null,
  conversationFriendlyId: string | null,
): TabEntity | null {
  switch (entityName) {
    case EntityNames.Conversation: {
      const conversationData = notification.parsedPayload?.conversation;
      if (!conversationFriendlyId || !conversationData) {
        return null;
      }

      return {
        type: TabType.Conversation,
        entity: {
          id: notification.entityId,
          conversationId: notification.entityId,
          friendlyId: conversationFriendlyId,
          partnerId: conversationData.partnerId,
          subject: notification.subject,
          unseenCount: 0,
        },
      };
    }
    case EntityNames.Site:
      return {
        type: TabType.Site,
        entity: {
          id: notification.entityId,
          addressId: notification.entityId,
          partnerId: notification.partnerId,
        },
      };
    case EntityNames.Client:
      return {
        type: TabType.Client,
        entity: {
          id: notification.entityId,
          clientId: notification.entityId,
          partnerId: notification.partnerId,
        },
      };
    default:
      return null;
  }
}

export const useNotificationNavigation = ({
  markOneAsRead,
}: {
  markOneAsRead: (notification: Notification) => void;
}) => {
  const { navigate, pathnameWithoutPrefix } = useNavigateRoute();
  const { openInTab } = useGlobalTabs();
  const { conversationId: selectedConversationId } = useSelectedConversation();

  const navigateToTarget = useCallback(
    (notification: Notification) => {
      const entityName = getNotificationEntityName(notification.entity);
      const conversationFriendlyId =
        entityName === EntityNames.Conversation
          ? getNotificationConversationFriendlyId(notification)
          : null;

      const path = getNotificationTargetPath({
        entityName,
        entityId: notification.entityId,
        conversationFriendlyId,
      });

      // platformUser (and any other unmapped entity) has no standard view to
      // open — the click still marks the notification read.
      if (!path) return;

      const tab = getNotificationTab(notification, entityName, conversationFriendlyId);
      if (tab) {
        openInTab(tab);
      }

      navigate(path);
    },
    [navigate, openInTab],
  );

  const handleItemClick = useMemo(
    () =>
      debounce((notification: Notification) => {
        markOneAsRead(notification);
        navigateToTarget(notification);
      }, ITEM_CLICK_DEBOUNCE_MS),
    [markOneAsRead, navigateToTarget],
  );

  const isRowSelected = useCallback(
    (notification: Notification) => {
      const entityName = getNotificationEntityName(notification.entity);

      if (entityName === EntityNames.Conversation) {
        return selectedConversationId === notification.entityId;
      }
      if (entityName === EntityNames.Site) {
        return pathnameWithoutPrefix === portalPaths.site.full(notification.entityId);
      }
      if (entityName === EntityNames.Client) {
        return pathnameWithoutPrefix === portalPaths.client.full(notification.entityId);
      }

      return false;
    },
    [pathnameWithoutPrefix, selectedConversationId],
  );

  return { navigateToTarget, handleItemClick, isRowSelected };
};
